// =====================================================================
// V4C Search Cloud Run proxy client
// =====================================================================
//
// 役割:
//   Cloud Run service mood-storefront-api の /search-proxy/{predictive,full}
//   を CORS 直叩きで呼び出す thin client。
//
//   V4C Retail Search API は API key 認証に対応していない (OAuth2 必須) ため、
//   ブラウザから retail.googleapis.com を直接叩くことはできず、Cloud Run 側で
//   service account credentials を使って中継する必要がある。
//
//   - V4C ranking 済み products[] (productId のみ) と server-side hydrated な
//     sectionHtml を取得
//   - attributionToken は localStorage に保存 (Web Pixel が読む)
//   - visitorId は GA4 _ga cookie の user_pseudo_id を proxy に vid= で渡す
//     (Cloud Run 側は vid 無ければ logged_in_customer_id / anon にフォールバック)
//
// 使う側:
//   - assets/v4c-predictive-search.js (Ctrl+K の predictive)
//   - assets/v4c-results-list.js (/search ページ)
//
// API 形状 (Cloud Run proxy):
//   GET <CLOUD_RUN_BASE>/search-proxy/predictive?q=...&limit=10&vid=...
//                                    &include_section=v4c-search-batch
//   GET <CLOUD_RUN_BASE>/search-proxy/full?q=...&page=1&page_size=24&vid=...
//                                    &include_section=v4c-search-batch
//   → { query, limit/page/pageSize, visitorId,
//        products: [{ productId, v4cId, rank }],
//        attributionToken, total, source: 'v4c'|'fallback-empty',
//        sectionHtml: '<完成 HTML>' (include_section 指定時のみ) }
//
//   Phase 3 (cleanup): handle / uri は不要なので response から撤去。
//   ブラウザ側で section render API を叩くときも `q=id:<productId>` 経由で
//   解決するため、handle 鮮度問題と無縁になる。
// =====================================================================

(function () {
  if (window.V4cClient) return;

  // Phase 3 / App Proxy 撤去:
  //   従来は Shopify App Proxy 経由 (`/apps/mood-storefront/search-proxy/*`) で
  //   Cloud Run service にルーティングしていたが、edge 往復で ~700-1000ms
  //   かかっていたため、ブラウザから Cloud Run service URL を直叩きに変更。
  //   Cloud Run 側の CORS middleware が MOOD storefront の Origin だけ許可する。
  var CLOUD_RUN_BASE = 'https://mood-storefront-api-332078478782.asia-northeast1.run.app';

  var SETTINGS = {
    PREDICTIVE_PATH: CLOUD_RUN_BASE + '/search-proxy/predictive',
    FULL_PATH: CLOUD_RUN_BASE + '/search-proxy/full',
    RECOMMEND_PATH: CLOUD_RUN_BASE + '/search-proxy/recommend',
    ATTR_TOKEN_KEY: 'v4c_attr_token',
    ATTR_TOKEN_AT_KEY: 'v4c_attr_token_at',
  };

  function getVisitorId() {
    try {
      // _ga cookie 形式: GA1.1.<user_pseudo_id>.<timestamp>
      var m = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.([^;]+)/);
      if (m) return m[1];
    } catch (_) {}
    return null;
  }

  function persistAttributionToken(token) {
    if (typeof token !== 'string' || token.length === 0) return;
    try {
      localStorage.setItem(SETTINGS.ATTR_TOKEN_KEY, token);
      localStorage.setItem(SETTINGS.ATTR_TOKEN_AT_KEY, String(Date.now()));
    } catch (_) {}
  }

  /**
   * proxy response の products[] を画面側で扱いやすい形に整形する。
   * 失敗 (productId 取得不可) は捨てる。
   *
   * Phase 3 cleanup: ブラウザ側の section render API は `q=id:<productId>`
   * 経由で叩くので、handle / url は不要。proxy も SearchResult.id (system
   * field) から productId を抽出するだけで完結し、catalog の uri retrievable
   * 設定にも依存しない。
   *
   * @param {Array<any>} rawProducts
   * @returns {Array<{ v4cId: string, productId: string, rank: number }>}
   */
  function normalizeProducts(rawProducts) {
    var out = [];
    if (!Array.isArray(rawProducts)) return out;
    for (var i = 0; i < rawProducts.length; i++) {
      var p = rawProducts[i] || {};
      if (!p.productId) continue;
      out.push({
        v4cId: p.v4cId || '',
        productId: String(p.productId),
        rank: typeof p.rank === 'number' ? p.rank : i,
      });
    }
    return out;
  }

  function buildUrl(path, params) {
    var url = new URL(path, location.origin);
    Object.keys(params).forEach(function (k) {
      if (params[k] !== undefined && params[k] !== null && params[k] !== '') {
        url.searchParams.set(k, String(params[k]));
      }
    });
    return url.toString();
  }

  /**
   * predictive (Ctrl+K dropdown) 用検索。Cloud Run proxy の /predictive を叩く。
   *
   * @param {{ query: string, limit?: number, visitorId?: string }} opts
   * @returns {Promise<{ results: ReturnType<typeof normalizeProducts>, attributionToken: string, totalSize: number, source: string }>}
   */
  async function searchPredictive(opts) {
    var query = (opts && opts.query) || '';
    var limit = (opts && opts.limit) || 10;
    var visitorId = (opts && opts.visitorId) || getVisitorId();
    var includeSection = (opts && opts.includeSection) || null;

    var url = buildUrl(SETTINGS.PREDICTIVE_PATH, {
      q: query,
      limit: limit,
      vid: visitorId,
      include_section: includeSection,
    });

    var res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error('v4c predictive proxy http ' + res.status);
    var json = await res.json();

    persistAttributionToken(json && json.attributionToken);

    return {
      results: normalizeProducts(json && json.products),
      attributionToken: (json && json.attributionToken) || '',
      totalSize: (json && typeof json.total === 'number') ? json.total : 0,
      source: (json && json.source) || '',
      // Phase 3 / server-side hydration: includeSection を渡した場合のみ proxy
      // が完成 HTML を返す。ブラウザはこれを innerHTML 置換するだけで完了。
      sectionHtml: (json && typeof json.sectionHtml === 'string') ? json.sectionHtml : '',
    };
  }

  /**
   * /search ページ用検索。Cloud Run proxy の /full を叩く。
   *
   * filters は Shopify Storefront Filtering API 形式の URL-encoded query string
   * (例: "filter.v.price.gte=10000&filter.v.product_type=Bag")。
   * proxy 側で Vertex AI Retail Search の filter field に変換される。
   *
   * @param {{ query: string, page?: number, pageSize?: number, visitorId?: string, includeSection?: string, filters?: string }} opts
   * @returns {Promise<{ results: ReturnType<typeof normalizeProducts>, attributionToken: string, totalSize: number, page: number, pageSize: number, source: string, sectionHtml: string }>}
   */
  async function searchFull(opts) {
    var query = (opts && opts.query) || '';
    var page = (opts && opts.page) || 1;
    var pageSize = (opts && opts.pageSize) || 24;
    var visitorId = (opts && opts.visitorId) || getVisitorId();
    var includeSection = (opts && opts.includeSection) || null;
    var filters = (opts && opts.filters) || '';

    var url = buildUrl(SETTINGS.FULL_PATH, {
      q: query,
      page: page,
      page_size: pageSize,
      vid: visitorId,
      include_section: includeSection,
      filters: filters,
    });

    var res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error('v4c full proxy http ' + res.status);
    var json = await res.json();

    persistAttributionToken(json && json.attributionToken);

    return {
      results: normalizeProducts(json && json.products),
      attributionToken: (json && json.attributionToken) || '',
      totalSize: (json && typeof json.total === 'number') ? json.total : 0,
      page: page,
      pageSize: pageSize,
      source: (json && json.source) || '',
      // Phase 3 / server-side hydration: includeSection を渡した場合のみ proxy
      // が完成 HTML を返す。ブラウザはこれを innerHTML 置換するだけで完了。
      sectionHtml: (json && typeof json.sectionHtml === 'string') ? json.sectionHtml : '',
      // V4C facets レスポンス。<v4c-facets-form> が filter UI 構築の source of truth
      // として使用。/predictive 等で facets が無いケースは空配列。
      facets: (json && Array.isArray(json.facets)) ? json.facets : [],
    };
  }

  /**
   * Storefront の locale を V4C の supported locale (ja/en/es/zh-CN/zh-TW) に正規化。
   * Shopify multilingual の <html lang="..."> 値から判定する。
   * proxy 側にも同じ正規化があるため、未指定でも動くが先に弾いた方が転送量少。
   */
  function getLocale() {
    try {
      var raw = (document.documentElement.lang || '').toLowerCase();
      if (raw.startsWith('zh-hans') || raw.startsWith('zh-cn')) return 'zh-CN';
      if (
        raw.startsWith('zh-hant') ||
        raw.startsWith('zh-tw') ||
        raw.startsWith('zh-hk') ||
        raw.startsWith('zh-mo')
      ) {
        return 'zh-TW';
      }
      if (raw.startsWith('en')) return 'en';
      if (raw.startsWith('es')) return 'es';
    } catch (_) {}
    return 'ja';
  }

  /**
   * V4C completeQuery 用。Cloud Run proxy の /complete を叩く。
   *
   * 補完候補 (autocomplete) を取得する。レスポンスの各 entry は
   * { text, type: 'brand'|'category'|'query', targetHandle: string|null }。
   * type が brand / category なら targetHandle に collection handle が入り、
   * frontend は `/collections/<targetHandle>` に直接遷移できる。query タイプは
   * targetHandle が null で、frontend は `/search?q=<text>` に遷移する。
   *
   * lang は <html lang> から自動判定 (ja/en/es/zh-CN/zh-TW)。明示指定したい場合は
   * opts.lang で上書き可能。
   *
   * @param {{ query: string, limit?: number, visitorId?: string, lang?: string }} opts
   * @returns {Promise<{ completions: Array<{text:string, type:string, targetHandle:string|null}>, attributionToken: string }>}
   */
  async function complete(opts) {
    var query = (opts && opts.query) || '';
    var limit = (opts && opts.limit) || 10;
    var visitorId = (opts && opts.visitorId) || getVisitorId();
    var lang = (opts && opts.lang) || getLocale();

    var url = buildUrl(CLOUD_RUN_BASE + '/search-proxy/complete', {
      q: query,
      limit: limit,
      vid: visitorId,
      lang: lang,
    });

    var res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error('v4c complete proxy http ' + res.status);
    var json = await res.json();

    persistAttributionToken(json && json.attributionToken);

    return {
      completions: Array.isArray(json && json.completions) ? json.completions : [],
      attributionToken: (json && json.attributionToken) || '',
    };
  }

  // 互換 API: 既存の `V4cClient.search({query, limit, offset})` 呼出にも対応 (Plan §6 当初想定)。
  // offset → page 換算は Phase 1c の v4c-results-list.js で full() 直叩きに統一する想定。
  async function search(opts) {
    var limit = (opts && opts.limit) || 10;
    var offset = (opts && opts.offset) || 0;
    var includeSection = (opts && opts.includeSection) || null;
    if (offset > 0 || limit > 20) {
      var pageSize = limit;
      var page = Math.floor(offset / pageSize) + 1;
      var r = await searchFull({ query: opts.query, page: page, pageSize: pageSize, visitorId: opts && opts.visitorId, includeSection: includeSection });
      return { results: r.results, attributionToken: r.attributionToken, totalSize: r.totalSize, sectionHtml: r.sectionHtml };
    }
    var p = await searchPredictive({ query: opts.query, limit: limit, visitorId: opts && opts.visitorId, includeSection: includeSection });
    return { results: p.results, attributionToken: p.attributionToken, totalSize: p.totalSize, sectionHtml: p.sectionHtml };
  }

  /**
   * recommended-for-you を叩く。POST body に行動シグナル (mood-view-history.js の
   * 閲覧履歴 + wishlist) を載せ、server 側エンジン (env RECOMMEND_ENGINE) が
   * custom (マルチアンカー embedding) か v4c (Retail Predict) かを決める。
   * productId 配列 + attributionToken を返す。商品カードの描画は呼び出し側
   * (v4c-recommendations.js) が section render API 経由で行う。
   *
   * @param {{ limit?: number }} opts
   * @returns {Promise<{ products: ReturnType<typeof normalizeProducts>, attributionToken: string, source: string }>}
   */
  async function recommend(opts) {
    opts = opts || {};
    var url = buildUrl(SETTINGS.RECOMMEND_PATH, { vid: getVisitorId() });
    var signals = window.MoodSignals || null;
    var res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({
        limit: opts.limit || 12,
        viewed: signals ? signals.getViewed() : [],
        wishlist: signals ? signals.getWishlist() : [],
      }),
    });
    if (!res.ok) throw new Error('v4c recommend proxy http ' + res.status);
    var json = await res.json();

    persistAttributionToken(json && json.attributionToken);

    return {
      products: normalizeProducts(json && json.products),
      attributionToken: (json && json.attributionToken) || '',
      source: (json && json.source) || '',
    };
  }

  window.V4cClient = {
    search: search,
    searchPredictive: searchPredictive,
    searchFull: searchFull,
    complete: complete,
    recommend: recommend,
    getVisitorId: getVisitorId,
    getLocale: getLocale,
    _settings: SETTINGS,
  };
})();
